/**
 * Designated-recipient-tab controller for cross-app navigation — Einstein side.
 *
 * When Galileo asks Einstein to open a deep-link (the load ribbon's Einstein
 * control), the intent is pushed to every Einstein connection of the user
 * (SignalR `Clients.User`). We must act on it in exactly one tab so opening a
 * load never hijacks an unrelated tab or fans out to all of them.
 *
 * The selection is a single shared slot in `localStorage` holding the chosen
 * tab's id (a per-tab id kept in `sessionStorage`). Because there is one slot,
 * designating a tab structurally un-designates every other — the single-target
 * invariant is enforced by the medium, not by coordination.
 *
 * Auto-designation: unlike Galileo (where the user picks the recipient tab via a
 * toggle), Einstein's recipient is the installed PWA. When the app is running in
 * standalone display-mode it designates itself on mount (see
 * {@link autoDesignateInstalledPwa}), so a click in Galileo lands in the PWA
 * without any manual setup. A plain browser tab does not auto-designate, so if
 * only browser tabs are open nothing acknowledges and Galileo falls back to
 * opening a tab — the correct graceful degradation.
 *
 * Liveness: a designated tab holds a well-known Web Lock for its lifetime. If it
 * closes without clearing the slot, the lock auto-releases; any tab can then
 * detect the stale slot (slot set but lock unheld) and clear it, so a closed
 * recipient never strands the feature. Web Locks is best-effort — absent it (old
 * browsers) the slot still works, just without stale-slot reclamation. The PWA
 * and browser tabs share one origin, so the slot and lock coordinate across all
 * of them. This mirrors Galileo's `einsteinRecipientTab`.
 */

const TAB_ID_KEY = "einstein:nav:tab-id";
const DESIGNATED_TAB_KEY = "einstein:nav:galileo-recipient-tab-id";
const RECIPIENT_LOCK = "einstein:nav:galileo-recipient";

let moduleTabId: string | null = null;
let lockReleaser: (() => void) | null = null;

function getSession(): Storage | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

function getLocal(): Storage | undefined {
    if (typeof window === "undefined") return undefined;
    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
}

function randomId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Stable id for this browsing context (one per tab/session). */
export function getTabId(): string {
    const session = getSession();
    if (session === undefined) {
        // No sessionStorage (SSR / privacy mode): fall back to an in-memory id so
        // the tab can still be designated for the lifetime of this module.
        moduleTabId ??= randomId();
        return moduleTabId;
    }
    let id = session.getItem(TAB_ID_KEY);
    if (id === null || id.length === 0) {
        id = randomId();
        session.setItem(TAB_ID_KEY, id);
    }
    return id;
}

function readDesignated(): string | null {
    return getLocal()?.getItem(DESIGNATED_TAB_KEY) ?? null;
}

/** Whether this tab is currently the designated recipient. */
export function isDesignatedTab(): boolean {
    return readDesignated() === getTabId();
}

/**
 * Acquire the liveness lock and hold it until designation is dropped. Held via a
 * promise the releaser resolves; `navigator.locks` auto-releases on tab close.
 */
function acquireLock(): void {
    if (lockReleaser !== null) return;
    if (typeof navigator === "undefined" || navigator.locks === undefined) return;
    let release!: () => void;
    const held = new Promise<void>((resolve) => {
        release = resolve;
    });
    lockReleaser = release;
    // ifAvailable: do not queue behind a stale holder; if another live tab holds
    // it we simply don't (a race two tabs rarely hit, resolved on next toggle).
    void navigator.locks
        .request(RECIPIENT_LOCK, { ifAvailable: true }, async (lock) => {
            if (lock === null) {
                lockReleaser = null;
                return;
            }
            await held;
        })
        .catch(() => {
            lockReleaser = null;
        });
}

function releaseLock(): void {
    lockReleaser?.();
    lockReleaser = null;
}

/** Designate (or un-designate) this tab as the Galileo-intent recipient. */
export function setDesignatedTab(designate: boolean): void {
    const local = getLocal();
    const tabId = getTabId();
    if (designate) {
        local?.setItem(DESIGNATED_TAB_KEY, tabId);
        acquireLock();
    } else if (readDesignated() === tabId) {
        local?.removeItem(DESIGNATED_TAB_KEY);
        releaseLock();
    }
}

/**
 * If the slot points at some tab but no tab holds the liveness lock, the prior
 * recipient closed without clearing it — reclaim by clearing the stale slot.
 * Safe to call on mount; a no-op when the slot is empty, ours, or genuinely held.
 */
export async function reclaimStaleDesignation(): Promise<void> {
    const designated = readDesignated();
    if (designated === null) return;
    if (designated === getTabId()) return; // our own live designation
    if (typeof navigator === "undefined" || navigator.locks === undefined) return;
    try {
        const state = await navigator.locks.query();
        const held = (state.held ?? []).some((l) => l.name === RECIPIENT_LOCK);
        if (!held && readDesignated() === designated) {
            getLocal()?.removeItem(DESIGNATED_TAB_KEY);
        }
    } catch {
        // query unsupported / denied: leave the slot as-is.
    }
}

/**
 * True when the app is running as an installed PWA (its own window / standalone
 * display-mode) rather than inside a browser tab. `display-mode: standalone`
 * matches the manifest's `display` setting; the legacy iOS `navigator.standalone`
 * flag is the fallback for home-screen web apps that predate the media query.
 */
export function isStandalonePwa(): boolean {
    if (typeof window === "undefined") return false;
    try {
        if (window.matchMedia?.("(display-mode: standalone)").matches === true) {
            return true;
        }
    } catch {
        // matchMedia unsupported / threw on the query string: fall through.
    }
    const legacy = window.navigator as Navigator & { standalone?: boolean };
    return legacy.standalone === true;
}

/**
 * Designate this tab as the Galileo-intent recipient when it is the installed
 * PWA. Idempotent and safe to call on every mount: a plain browser tab is a
 * no-op (it never claims designation), while the PWA claims the single slot so
 * peer-navigation from Galileo lands here. Later-mounting standalone windows win
 * the slot; the single-target invariant still holds because only the tab whose id
 * matches the slot acts on an intent.
 */
export function autoDesignateInstalledPwa(): void {
    if (!isStandalonePwa()) return;
    setDesignatedTab(true);
}
